import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { PageShell } from '@/components/ui/page-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { canViewFinancials } from '@/lib/permissions';
import {
  ArrowLeft,
  Briefcase,
  Scale,
  Users,
  Euro,
  Pencil,
  Clock,
  FileText,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  User,
  Printer,
  Receipt,
} from 'lucide-react';
import {
  MatterFormDialog,
  FeeSharingPanel,
} from '@/components/matters';
import api from '@/lib/api';
import { Matter } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
// useUIConfig no longer needed — PageShell handles width/spacing.

// ─── Types ──────────────────────────────────────────────────────────────────────

interface UserBreakdown {
  user_id: number;
  name: string;
  fee: number;
  hours: number;
  billed_fee: number;
}

interface PhaseStats {
  id: number;
  name: string;
  description: string;
  budget: number;
  order: number;
  start_date: string | null;
  end_date: string | null;
  spent: number;
  hours: number;
  billed: number;
  billed_hours: number;
  unbilled: number;
  utilisation: number;
  remaining: number;
  user_breakdown: UserBreakdown[];
}

interface MatterCost {
  id: number;
  date: string;
  category: string;
  description: string;
  amount: number;
  billable: boolean;
  invoiced: boolean;
}

interface PhaseStatsResponse {
  phases: PhaseStats[];
  unphased: {
    spent: number;
    hours: number;
  };
  totals: {
    budget: number;
    spent: number;
    hours: number;
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

const currencyFmt = new Intl.NumberFormat('en-BE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyFmtDetailed = new Intl.NumberFormat('en-BE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const hoursFmt = (h: number) =>
  `${h.toFixed(1)}h`;

const dateFmt = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

function utilizationColor(pct: number): string {
  if (pct >= 100) return 'text-red-600 dark:text-red-400';
  if (pct >= 80) return 'text-amber-600 dark:text-amber-400';
  return 'text-emerald-600 dark:text-emerald-400';
}

function utilizationBg(pct: number): string {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function utilizationBadge(pct: number) {
  if (pct >= 100) {
    return (
      <Badge variant="destructive" className="text-xs gap-1">
        <AlertTriangle className="h-3 w-3" />
        Over budget
      </Badge>
    );
  }
  if (pct >= 80) {
    return (
      <Badge className="text-xs gap-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100">
        <TrendingUp className="h-3 w-3" />
        {pct.toFixed(0)}% used
      </Badge>
    );
  }
  return (
    <Badge className="text-xs gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100">
      <CheckCircle2 className="h-3 w-3" />
      {pct.toFixed(0)}% used
    </Badge>
  );
}

function useCanViewFinancials(): boolean {
  const user = useAuth((s) => s.user);
  const groups = user?.groups ?? [];
  return canViewFinancials(groups);
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function MatterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const financials = useCanViewFinancials();

  const [matter, setMatter] = useState<Matter | null>(null);
  const [phaseStats, setPhaseStats] = useState<PhaseStatsResponse | null>(null);
  const [costs, setCosts] = useState<MatterCost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());

  const fetchMatter = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [matterRes, statsRes, costsRes] = await Promise.all([
        api.get(`${ROUTES.API.MATTERS}${id}/`),
        api.get(`${ROUTES.API.MATTERS}${id}/phase-stats/`),
        api.get(`${ROUTES.API.COSTS}?matter=${id}&limit=9999`),
      ]);
      setMatter(matterRes.data as Matter);
      setPhaseStats(statsRes.data as PhaseStatsResponse);
      setCosts((costsRes.data.results || []) as MatterCost[]);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load matter details.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    fetchMatter();
  }, [fetchMatter]);

  const handleEditSuccess = () => {
    fetchMatter();
  };

  const togglePhaseExpand = (phaseId: number) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };

  // ── Loading State ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Layout>
        <PageShell className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-96" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </PageShell>
      </Layout>
    );
  }

  if (!matter) {
    return (
      <Layout>
        <PageShell className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.MATTERS)}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Matters
            </Button>
          </div>
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Matter not found.</p>
            </CardContent>
          </Card>
        </PageShell>
      </Layout>
    );
  }

  const totalOverallUtilisation =
    phaseStats && phaseStats.totals.budget > 0
      ? (phaseStats.totals.spent / phaseStats.totals.budget) * 100
      : 0;

  const costTotals = costs.reduce(
    (acc, c) => {
      const amount = Number(c.amount) || 0;
      acc.total += amount;
      if (c.billable) acc.billable += amount;
      if (c.invoiced) acc.invoiced += amount;
      return acc;
    },
    { total: 0, billable: 0, invoiced: 0 },
  );

  return (
    <Layout>
      <PageShell className="space-y-6">
        {/* Breadcrumb / Back */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.MATTERS)}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Matters
          </Button>
        </div>

        {/* Matter Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Briefcase className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{matter.name}</h2>
                {matter.group_name && (
                  <p className="text-sm text-muted-foreground">{matter.group_name}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={matter.status === 'active' ? 'default' : 'secondary'}
              className={
                matter.status === 'active'
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : ''
              }
            >
              {matter.status === 'active' ? (
                <CheckCircle2 className="h-3 w-3 mr-1" />
              ) : (
                <Clock className="h-3 w-3 mr-1" />
              )}
              {matter.status === 'active' ? 'Active' : 'Inactive'}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const token = localStorage.getItem('accessToken');
                const base = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
                const url = `${base}${ROUTES.API.MATTERS}${matter.id}/report-pdf/`;
                // Open in new tab with auth
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((res) => res.blob())
                  .then((blob) => {
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                  })
                  .catch(() =>
                    toast({ title: 'Error', description: 'Failed to generate report.', variant: 'destructive' })
                  );
              }}
            >
              <Printer className="h-4 w-4 mr-1.5" />
              Print Report
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)}>
              <Pencil className="h-4 w-4 mr-1.5" />
              Edit Matter
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Users className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Clients</p>
                  <p className="text-lg font-semibold truncate">
                    {(() => {
                      const activeConfig =
                        matter.fee_sharing_configs?.find((c) => c.is_active) ||
                        matter.fee_sharing_configs?.[0];
                      return activeConfig?.participants?.length || 0;
                    })()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Euro className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Budget</p>
                  <p className="text-lg font-semibold truncate">
                    {matter.budget ? currencyFmt.format(matter.budget) : '—'}
                  </p>
                  {phaseStats && phaseStats.totals.budget > 0 && (
                    <p className={`text-xs font-medium ${utilizationColor(totalOverallUtilisation)}`}>
                      {currencyFmtDetailed.format(phaseStats.totals.spent)} spent
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Scale className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Billable</p>
                  <p className="text-lg font-semibold">{matter.billable ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Rounding</p>
                  <p className="text-lg font-semibold">
                    {matter.rounding
                      ? `${matter.rounding_type} / ${matter.rounding_amount}m`
                      : 'Off'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overall Budget Utilisation Bar */}
        {phaseStats && phaseStats.totals.budget > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Overall Budget Utilisation
                  </CardTitle>
                  <CardDescription>
                    {currencyFmtDetailed.format(phaseStats.totals.spent)} of{' '}
                    {currencyFmt.format(phaseStats.totals.budget)} spent ·{' '}
                    {hoursFmt(phaseStats.totals.hours)} billed
                  </CardDescription>
                </div>
                {utilizationBadge(totalOverallUtilisation)}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="relative h-4 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${utilizationBg(
                      totalOverallUtilisation
                    )}`}
                    style={{ width: `${Math.min(totalOverallUtilisation, 100)}%` }}
                  />
                  {totalOverallUtilisation > 100 && (
                    <div
                      className="absolute top-0 h-full rounded-r-full bg-red-600/30 border-l-2 border-red-600"
                      style={{
                        left: '100%',
                        width: '0px',
                      }}
                    />
                  )}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{totalOverallUtilisation.toFixed(1)}% used</span>
                  <span>
                    {currencyFmtDetailed.format(
                      phaseStats.totals.budget - phaseStats.totals.spent
                    )}{' '}
                    remaining
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Costs / Expenses booked on this matter */}
        {financials && costs.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Receipt className="h-5 w-5 text-primary" />
                    Costs &amp; Expenses
                  </CardTitle>
                  <CardDescription>
                    {costs.length} {costs.length === 1 ? 'entry' : 'entries'} ·{' '}
                    {currencyFmtDetailed.format(costTotals.billable)} billable ·{' '}
                    {currencyFmtDetailed.format(costTotals.total - costTotals.invoiced)} not yet
                    invoiced
                  </CardDescription>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xl font-semibold">
                    {currencyFmtDetailed.format(costTotals.total)}
                  </p>
                  <p className="text-xs text-muted-foreground">total costs</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-2 pr-3 text-left font-medium">Date</th>
                      <th className="py-2 pr-3 text-left font-medium">Category</th>
                      <th className="py-2 pr-3 text-left font-medium">Description</th>
                      <th className="py-2 pr-3 text-left font-medium">Status</th>
                      <th className="py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.map((cost) => (
                      <tr key={cost.id} className="border-b last:border-0">
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {dateFmt(cost.date?.slice(0, 10) ?? null)}
                        </td>
                        <td className="py-2 pr-3">{cost.category || '—'}</td>
                        <td className="py-2 pr-3 max-w-[28rem] truncate" title={cost.description}>
                          {cost.description || '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1">
                            {!cost.billable && (
                              <Badge variant="outline" className="text-[10px]">
                                Non-billable
                              </Badge>
                            )}
                            <Badge
                              variant={cost.invoiced ? 'secondary' : 'outline'}
                              className="text-[10px]"
                            >
                              {cost.invoiced ? 'Invoiced' : 'Open'}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-2 text-right font-medium whitespace-nowrap">
                          {currencyFmtDetailed.format(Number(cost.amount) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phases with Budget Utilisation */}
        {phaseStats && phaseStats.phases.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-primary" />
                Phase Budget Breakdown
              </CardTitle>
              <CardDescription>
                Per-phase budget utilisation based on timesheet data within each phase's date range
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-4">
                {phaseStats.phases
                  .sort((a, b) => a.order - b.order)
                  .map((phase) => {
                    const hasDates = phase.start_date || phase.end_date;
                    const pct = phase.budget > 0 ? (phase.spent / phase.budget) * 100 : 0;
                    const isExpanded = expandedPhases.has(phase.id);

                    // Segmented bar calculations
                    const budget = phase.budget;
                    const billed = phase.billed ?? 0;
                    const spent = phase.spent;
                    const barMax = budget > 0 ? Math.max(budget, spent) : spent;
                    const billedPct = barMax > 0 ? (billed / barMax) * 100 : 0;
                    const withinBudgetUnbilled = budget > 0 ? Math.max(0, Math.min(spent, budget) - billed) : Math.max(0, spent - billed);
                    const withinBudgetPct = barMax > 0 ? (withinBudgetUnbilled / barMax) * 100 : 0;
                    const overBudget = budget > 0 ? Math.max(0, spent - budget) : 0;
                    const overBudgetPct = barMax > 0 ? (overBudget / barMax) * 100 : 0;
                    const budgetLinePct = barMax > 0 && budget > 0 ? (budget / barMax) * 100 : 100;

                    return (
                      <div
                        key={phase.id}
                        className="rounded-lg border p-4 space-y-3"
                      >
                        {/* Phase Header */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm">{phase.name}</p>
                              {phase.budget > 0 && utilizationBadge(pct)}
                            </div>
                            {phase.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {phase.description}
                              </p>
                            )}
                            {hasDates && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <CalendarDays className="h-3 w-3" />
                                {phase.start_date && phase.end_date
                                  ? `${dateFmt(phase.start_date)} – ${dateFmt(phase.end_date)}`
                                  : phase.start_date
                                    ? `From ${dateFmt(phase.start_date)}`
                                    : `Until ${dateFmt(phase.end_date)}`}
                              </p>
                            )}
                            {!hasDates && (
                              <p className="text-xs text-muted-foreground/60 italic mt-1">
                                No date range set — timesheets not attributed
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-semibold text-sm">
                              {currencyFmt.format(phase.budget)}
                            </p>
                            <p className="text-xs text-muted-foreground">budget</p>
                          </div>
                        </div>

                        {/* Segmented Utilisation Bar */}
                        {hasDates && phase.budget > 0 && (
                          <div className="space-y-1.5">
                            <div className="relative h-3 w-full rounded-full bg-muted">
                              {/* Blue: billed / invoiced */}
                              {billedPct > 0 && (
                                <div
                                  className="absolute top-0 left-0 h-full rounded-l-full bg-blue-500 transition-all duration-500"
                                  style={{ width: `${Math.min(billedPct, 100)}%` }}
                                  title={`Billed: ${currencyFmtDetailed.format(billed)}`}
                                />
                              )}
                              {/* Green: unbilled within budget */}
                              {withinBudgetPct > 0 && (
                                <div
                                  className="absolute top-0 h-full bg-emerald-500 transition-all duration-500"
                                  style={{
                                    left: `${billedPct}%`,
                                    width: `${Math.min(withinBudgetPct, 100 - billedPct)}%`,
                                  }}
                                  title={`Unbilled (within budget): ${currencyFmtDetailed.format(withinBudgetUnbilled)}`}
                                />
                              )}
                              {/* Red: over budget */}
                              {overBudgetPct > 0 && (
                                <div
                                  className="absolute top-0 h-full rounded-r-full bg-red-500 transition-all duration-500"
                                  style={{
                                    left: `${budgetLinePct}%`,
                                    width: `${overBudgetPct}%`,
                                  }}
                                  title={`Over budget: ${currencyFmtDetailed.format(overBudget)}`}
                                />
                              )}
                              {/* Budget line marker */}
                              {spent > budget && budget > 0 && (
                                <div
                                  className="absolute top-[-3px] h-[calc(100%+6px)] w-[2px] bg-foreground/70 z-10"
                                  style={{ left: `${budgetLinePct}%` }}
                                  title={`Budget: ${currencyFmt.format(budget)}`}
                                />
                              )}
                              {/* Round right end of the bar when not overbudget */}
                              {overBudgetPct === 0 && (billedPct + withinBudgetPct) > 0 && (
                                <div
                                  className="absolute top-0 h-full rounded-r-full"
                                  style={{
                                    left: `${billedPct + withinBudgetPct - 0.5}%`,
                                    width: '0.5%',
                                    background: withinBudgetPct > 0 ? 'rgb(16 185 129)' : 'rgb(59 130 246)',
                                  }}
                                />
                              )}
                            </div>

                            {/* Legend row */}
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                              {billed > 0 && (
                                <span className="flex items-center gap-1">
                                  <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                                  Billed: {currencyFmtDetailed.format(billed)}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                                <span className={`font-medium ${utilizationColor(pct)}`}>
                                  {currencyFmtDetailed.format(phase.spent)}
                                </span>{' '}
                                spent · {hoursFmt(phase.hours)}
                              </span>
                              {overBudget > 0 && (
                                <span className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                                  <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
                                  {currencyFmtDetailed.format(overBudget)} over budget
                                </span>
                              )}
                              {phase.remaining >= 0 && (
                                <span className="ml-auto">
                                  {currencyFmtDetailed.format(phase.remaining)} remaining
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* When dates are set but no budget */}
                        {hasDates && phase.budget === 0 && phase.spent > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {currencyFmtDetailed.format(phase.spent)} spent ·{' '}
                            {hoursFmt(phase.hours)} (no budget set)
                          </div>
                        )}

                        {/* Per-user breakdown toggle */}
                        {hasDates && phase.user_breakdown && phase.user_breakdown.length > 0 && (
                          <div>
                            <button
                              type="button"
                              onClick={() => togglePhaseExpand(phase.id)}
                              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5" />
                              )}
                              <User className="h-3 w-3" />
                              {phase.user_breakdown.length} team member{phase.user_breakdown.length !== 1 ? 's' : ''}
                            </button>

                            {isExpanded && (
                              <div className="mt-2 rounded-md border bg-muted/20 overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b bg-muted/40">
                                      <th className="text-left py-1.5 px-3 font-medium text-muted-foreground">Name</th>
                                      <th className="text-right py-1.5 px-3 font-medium text-muted-foreground">Fee</th>
                                      <th className="text-right py-1.5 px-3 font-medium text-muted-foreground">Billed</th>
                                      <th className="text-right py-1.5 px-3 font-medium text-muted-foreground">Hours</th>
                                      <th className="text-right py-1.5 px-3 font-medium text-muted-foreground">Share</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {phase.user_breakdown.map((u) => {
                                      const sharePct = phase.spent > 0 ? (u.fee / phase.spent) * 100 : 0;
                                      return (
                                        <tr key={u.user_id} className="border-b last:border-0 hover:bg-muted/30">
                                          <td className="py-1.5 px-3 font-medium">{u.name}</td>
                                          <td className="py-1.5 px-3 text-right">{currencyFmtDetailed.format(u.fee)}</td>
                                          <td className="py-1.5 px-3 text-right text-blue-600 dark:text-blue-400">
                                            {currencyFmtDetailed.format(u.billed_fee)}
                                          </td>
                                          <td className="py-1.5 px-3 text-right">{hoursFmt(u.hours)}</td>
                                          <td className="py-1.5 px-3 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                              <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                                                <div
                                                  className="h-full rounded-full bg-primary/60"
                                                  style={{ width: `${Math.min(sharePct, 100)}%` }}
                                                />
                                              </div>
                                              <span className="w-10 text-right">{sharePct.toFixed(0)}%</span>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                {/* Unphased timesheets */}
                {phaseStats.unphased.spent > 0 && (
                  <div className="rounded-lg border border-dashed p-4 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-muted-foreground">
                          Unphased Timesheets
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Entries not covered by any phase date range
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm">
                          {currencyFmtDetailed.format(phaseStats.unphased.spent)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {hoursFmt(phaseStats.unphased.hours)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phase totals */}
                <Separator />
                <div className="flex justify-between text-sm px-1">
                  <span className="text-muted-foreground">Total phase budgets:</span>
                  <span className="font-semibold">
                    {currencyFmt.format(
                      phaseStats.phases.reduce((sum, p) => sum + p.budget, 0)
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fee Sharing Panel — only visible to privileged users */}
        {financials && (
          <>
            <Separator />
            <FeeSharingPanel matterId={matter.id} />
          </>
        )}

        {/* Edit Dialog */}
        <MatterFormDialog
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          matter={matter}
          onSuccess={handleEditSuccess}
        />
      </PageShell>
    </Layout>
  );
}
